import {CommonModule} from '@angular/common';
import {Component, Input} from '@angular/core';
import {ReactiveFormsModule} from '@angular/forms';
import {AmountPipe, EmTranslatePipe} from '@emma-labs/shared/angular';
import {Fna, TextTranslations} from '@emma-labs/shared/typescript';
import {DividerModule} from 'primeng/divider';
import {DropdownModule} from 'primeng/dropdown';
import {InputTextModule} from 'primeng/inputtext';
import {TableModule} from 'primeng/table';

interface Need {
  title: TextTranslations;
  amount: number;
}

interface FnaSummary {
  temporaryNeeds?: {needs: Need[]; total: number};
  permanentNeeds?: {needs: Need[]; total: number};
  disabilityNeeds?: number;
  cancerNeeds?: number;
  totalNeeds?: number;
  isComplete?: boolean;
}

@Component({
  selector: 'em-needs-section',
  templateUrl: './needs-section.component.html',
  styleUrls: ['./needs.section.component.scss'],
  imports: [
    DividerModule,
    CommonModule,
    DropdownModule,
    InputTextModule,
    ReactiveFormsModule,
    TableModule,
    AmountPipe,
    EmTranslatePipe,
  ],
})
export class NeedsSectionComponent {
  _fna: Fna | undefined;
  fnaSummary: FnaSummary | undefined;
  totalCurrentProtections: number | undefined;

  @Input({required: true}) set fna(fna: Fna) {
    this.fnaSummary = this.getFnaSummary(fna);
  }

  private getFnaSummary(fna: Fna): FnaSummary {
    const temporaryNeeds = this.getTemporaryNeeds(fna);
    const permanentNeeds = this.getPermanentNeeds(fna);

    return {
      temporaryNeeds,
      permanentNeeds,
      disabilityNeeds: fna.disabilityNeeds,
      cancerNeeds: fna.cancerNeeds,
      totalNeeds: fna.needs,
      isComplete: fna.isComplete,
    };
  }

  private getTemporaryNeeds(fna: Fna): {needs: Need[]; total: number} | undefined {
    const needs: Need[] = [];

    if (fna.mortgageAmount) {
      needs.push({title: {en: 'Mortgage(s)', fr: 'Hypothèque(s)'}, amount: fna.mortgageAmount});
    }

    if (fna.debtAmount) {
      needs.push({title: {en: 'Debt', fr: 'Dettes'}, amount: fna.debtAmount});
    }

    if (fna.salaryReplacement) {
      needs.push({
        title: {en: 'Salary Replacement', fr: 'Remplacement de salaire'},
        amount: fna.salaryReplacement,
      });
    }

    if (fna.savingsAmount) {
      needs.push({title: {en: 'Savings', fr: 'Économies'}, amount: -fna.savingsAmount});
    }

    const totalCurrentProtections = fna.currentProtections?.reduce(
      (acc, curr) => acc + (curr.coverageAmount ?? 0),
      0,
    );

    if (totalCurrentProtections) {
      needs.push({
        title: {en: 'Current Protections', fr: 'Protections actuelles'},
        amount: -totalCurrentProtections,
      });
    }

    if (!needs.length) {
      return undefined;
    }

    return {
      needs,
      total: fna.temporaryNeeds ?? needs.reduce((acc, curr) => acc + curr.amount, 0),
    };
  }

  private getPermanentNeeds(fna: Fna): {needs: Need[]; total: number} | undefined {
    const needs: Need[] = [];

    if (fna.funeralCost) {
      needs.push({title: {en: 'Funeral Cost', fr: 'Frais funéraires'}, amount: fna.funeralCost});
    }

    if (fna.heritageAmount) {
      needs.push({title: {en: 'Heritage', fr: 'Héritage'}, amount: fna.heritageAmount});
    }

    if (!needs.length) {
      return undefined;
    }

    return {
      needs,
      total: fna.permanentNeeds ?? needs.reduce((acc, curr) => acc + curr.amount, 0),
    };
  }
}
